import React from 'react';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
} from '@dnd-kit/core';
import type { SensorDescriptor, SensorOptions } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableContentList } from '@org/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@org/ui';
import { Badge } from '@org/ui';
import { Alert, AlertTitle, AlertDescription } from '@org/ui';
import { InfoIcon } from 'lucide-react';

type SortableItemsCardProps<T> = {
  items: T[];
  sensors: SensorDescriptor<SensorOptions>[];
  onDragEnd: (event: DragEndEvent) => void;
  onRemove: (item: T) => void;
  idKey: string;
  nameKey: string;
  descriptionKey: string;
  title: string;
  emptyTitle: string;
  emptyDescription: string;
};

export function SortableItemsCard<T>({
  items,
  sensors,
  onDragEnd,
  onRemove,
  idKey,
  nameKey,
  descriptionKey,
  title,
  emptyTitle,
  emptyDescription,
}: SortableItemsCardProps<T>) {
  if (items.length === 0) {
    return (
      <Alert variant="info" className="my-4">
        <InfoIcon className="h-4 w-4" />
        <AlertTitle>{emptyTitle}</AlertTitle>
        <AlertDescription>{emptyDescription}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="mb-6 shadow-sm">
      <CardHeader className="bg-gray-50 rounded-t-md border-b pb-3 pt-3">
        <div className="flex justify-between items-center">
          <CardTitle className="text-base font-medium text-gray-800">
            {title} ({items.length})
          </CardTitle>
          <Badge variant="outline">Drag to reorder</Badge>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={items.map((item: any, idx: number) => item[idKey] ?? `item-${idx}`)}
            strategy={verticalListSortingStrategy}
          >
            <SortableContentList
              items={items}
              onRemove={onRemove}
              idKey={idKey}
              nameKey={nameKey}
              descriptionKey={descriptionKey}
            />
          </SortableContext>
        </DndContext>
      </CardContent>
    </Card>
  );
}
